/**
 * Client-side compliance labels for equipment rows (mirrors API complianceSummary).
 */

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** @typedef {'none' | 'ok' | 'warning' | 'expired'} ComplianceDateStatus */

/**
 * Classify a compliance-related date for UI (expired, due soon, or OK).
 * @param {string|Date|null|undefined} date
 * @param {Date} [now]
 * @param {number} [warningDays]
 * @returns {ComplianceDateStatus}
 */
export const getComplianceDateStatus = (
  date,
  now = new Date(),
  warningDays = 30
) => {
  if (!date) return "none";
  const today = startOfDay(now);
  const target = startOfDay(date);
  if (target < today) return "expired";
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() + warningDays);
  if (target <= cutoff) return "warning";
  return "ok";
};

/**
 * Signed day count until a date (negative = past).
 * @param {string|Date|null|undefined} date
 * @param {Date} [now]
 * @returns {number|null}
 */
export const getDaysUntil = (date, now = new Date()) => {
  if (!date) return null;
  const today = startOfDay(now);
  const target = startOfDay(date);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((target - today) / msPerDay);
};

/**
 * Human-readable relative label for compliance dates.
 * @param {string|Date|null|undefined} date
 * @param {Date} [now]
 * @returns {string}
 */
export const formatComplianceRelative = (date, now = new Date()) => {
  const days = getDaysUntil(date, now);
  if (days === null) return "Not set";
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `Due in ${days} days`;
};

const STATUS_BADGE_COLOR = {
  expired: "red",
  warning: "yellow",
  ok: "green",
  none: "gray",
};

export const getComplianceDateBadgeColor = (status) =>
  STATUS_BADGE_COLOR[status] || "gray";

export const getEquipmentComplianceAlerts = (equipment, now = new Date()) => {
  const today = startOfDay(now);
  const alerts = [];

  if (
    equipment?.registrationExpiry &&
    startOfDay(equipment.registrationExpiry) < today
  ) {
    alerts.push({ key: "registration", label: "Reg. expired", color: "red" });
  }
  if (
    equipment?.insuranceExpiry &&
    startOfDay(equipment.insuranceExpiry) < today
  ) {
    alerts.push({ key: "insurance", label: "Insurance expired", color: "red" });
  }
  if (
    equipment?.nextInspectionDue &&
    startOfDay(equipment.nextInspectionDue) < today
  ) {
    alerts.push({ key: "inspection", label: "Inspection overdue", color: "red" });
  } else if (equipment?.nextInspectionDue) {
    const due = startOfDay(equipment.nextInspectionDue);
    const cutoff = new Date(today);
    cutoff.setDate(cutoff.getDate() + 30);
    if (due >= today && due <= cutoff) {
      alerts.push({ key: "inspectionSoon", label: "Inspection due soon", color: "yellow" });
    }
  }

  return alerts;
};
